import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Link,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import toast from 'react-hot-toast';
import FieldCodePanel from './FieldCodePanel';
import StaffKeyPanel from './StaffKeyPanel';
import RunLogPanel from './RunLogPanel';
import { Page } from '../../components/page/Page';
import FieldKitAdminTools from './FieldKitAdminTools';

function FieldKitReports({ user }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ last24h: 0, unmatched24h: 0 });
  const [loading, setLoading] = useState(true);
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [registering, setRegistering] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const q = unmatchedOnly ? '?unmatched=1&limit=200' : '?limit=200';
      const res = await api.get(`/field-reports${q}`);
      setRows(res.data.reports || []);
      setStats(res.data.stats || { last24h: 0, unmatched24h: 0 });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load field reports');
    }
    setLoading(false);
  }, [unmatchedOnly]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const handleRegister = async (row) => {
    setRegistering(row._id);
    try {
      const res = await api.post(`/field-reports/${row._id}/register`, {});
      toast.success('Screen registered');
      if (res.data.quickSetupUrl) {
        navigate(res.data.quickSetupUrl);
      } else {
        fetchReports();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Register failed');
    }
    setRegistering(null);
  };

  return (
    <Layout title="Field Visits" user={user}>
      <Page
        icon={<BuildIcon sx={{ color: '#551FFF' }} />}
        title="Field Kit Visits"
        meta={
          <>
            <Chip size="small" label={`24h: ${stats.last24h}`} />
            <Chip size="small" color="warning" variant="outlined" label={`Unregistered: ${stats.unmatched24h}`} />
          </>
        }
        actions={
          <Button size="small" href="/field-kit/" target="_blank" rel="noopener" endIcon={<OpenInNewIcon />}>
            Public download page
          </Button>
        }
      >
        {user?.role === 'admin' && <FieldCodePanel />}
        {user?.role === 'admin' && <StaffKeyPanel />}
        {user?.role === 'admin' && <RunLogPanel />}
        {user?.role === 'admin' && <FieldKitAdminTools />}

        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Store PCs send device MAC, code, and APK results after running the field kit. Unregistered TVs appear as{' '}
            <strong>discovered</strong> screens — use <strong>Register</strong> then finish in Quick Setup.
          </Typography>
          <FormControlLabel
            control={<Switch checked={unmatchedOnly} onChange={(e) => setUnmatchedOnly(e.target.checked)} />}
            label="Show only not yet registered (registered status)"
          />
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>MAC</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Chipset</TableCell>
                  <TableCell>APK</TableCell>
                  <TableCell>Screen</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No field reports yet.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => {
                  const reg = r.screen?.registrationStatus;
                  const isRegistered = reg === 'registered';
                  return (
                    <TableRow key={r._id} hover>
                      <TableCell>{fmt(r.createdAt)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.deviceCode || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.hwMac || '—'}</TableCell>
                      <TableCell>{r.model || '—'}</TableCell>
                      <TableCell>{r.chipset || '—'}</TableCell>
                      <TableCell>
                        {r.installOk ? (
                          <Chip size="small" color="success" label={r.apkInstalledVersion || r.appVersion || 'OK'} />
                        ) : (
                          <Chip size="small" color="error" label="Install fail" />
                        )}
                      </TableCell>
                      <TableCell>
                        {r.screen ? (
                          <>
                            <Typography variant="body2">{r.screen.name || r.screen.code}</Typography>
                            <Chip
                              size="small"
                              label={reg || 'unknown'}
                              color={isRegistered ? 'success' : 'warning'}
                              sx={{ mt: 0.5 }}
                            />
                          </>
                        ) : (
                          <Chip size="small" label="No screen" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!isRegistered && r.screenId && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={registering === r._id}
                            onClick={() => handleRegister(r)}
                            sx={{ bgcolor: '#551FFF', mr: 0.5 }}
                          >
                            Register
                          </Button>
                        )}
                        {r.deviceCode && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/quick-setup?deviceCode=${r.deviceCode}`)}
                          >
                            Quick Setup
                          </Button>
                        )}
                        {r.screenId && (
                          <Link href={`/screen`} sx={{ ml: 1, fontSize: 13 }}>
                            Screens
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Page>
    </Layout>
  );
}

export default FieldKitReports;
